import React, { useState, useEffect } from 'react';
import { Tag, Plus, Edit2, Trash2, Loader2, Search, AlertCircle, X, Check, Bookmark } from 'lucide-react';
import { API_BASE_URL } from '../../config';

const TicketCategories = ({ authToken }) => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentCategory, setCurrentCategory] = useState({ id: null, name: '', price: '', description: '' });

    useEffect(() => {
        fetchCategories();
    }, [authToken]);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/admin/super/ticket-pricing`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            if (response.ok) {
                const data = await response.json();
                setCategories(data.categories || (Array.isArray(data) ? data : []));
            } else if (response.status === 404) {
                // Initial Default pricing levels as per user's logic
                setCategories([
                    { id: 1, name: 'VIP Ticket', price: 65, description: 'Premium access with front-row seating and benefits' },
                    { id: 2, name: 'Medium Ticket', price: 45, description: 'Standard seating with regular entry' },
                    { id: 3, name: 'General Entry', price: 25, description: 'Basic entry to the exhibition' }
                ]);
            }
        } catch (err) {
            setError('Failed to fetch pricing categories');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            setActionLoading(true);
            const method = currentCategory.id ? 'PUT' : 'POST';
            const url = currentCategory.id
                ? `${API_BASE_URL}/admin/super/ticket-pricing/${currentCategory.id}`
                : `${API_BASE_URL}/admin/super/ticket-pricing`;

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify(currentCategory)
            });

            if (response.ok) {
                fetchCategories();
                setIsModalOpen(false);
                setCurrentCategory({ id: null, name: '', price: '', description: '' });
            } else {
                setError('Failed to save pricing level');
            }
        } catch (err) {
            setError('Failed to save pricing level');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this pricing category?')) return;
        try {
            setActionLoading(true);
            const response = await fetch(`${API_BASE_URL}/admin/super/ticket-pricing/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${authToken}` }
            });
            if (response.ok) {
                fetchCategories();
            } else {
                setError('Failed to delete pricing');
            }
        } catch (err) {
            setError('Failed to delete pricing');
        } finally {
            setActionLoading(false);
        }
    };

    const filteredCategories = categories.filter(cat =>
        cat.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Ticket Pricing Management</h2>
                    <p className="text-sm text-gray-500 mt-1">Set fixed prices for different event ticket categories.</p>
                </div>
                <button
                    onClick={() => {
                        setCurrentCategory({ id: null, name: '', price: '', description: '' });
                        setIsModalOpen(true);
                    }}
                    className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all font-bold text-sm shadow-sm"
                >
                    <Plus className="h-4 w-4 mr-2" /> Define New Price Level
                </button>
            </div>

            <div className="bg-white rounded-2xl shadow-lg shadow-gray-100 overflow-hidden border border-gray-100">
                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-100">
                            <thead className="bg-gray-50/50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Ticket Type</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Base Price</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Description</th>
                                    <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-widest">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-50">
                                {filteredCategories.map((cat) => (
                                    <tr key={cat.id} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center font-bold text-gray-900">
                                                <Bookmark className="h-4 w-4 text-indigo-500 mr-2" />
                                                {cat.name}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-black text-emerald-600">₹{cat.price}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-xs text-gray-400 line-clamp-1">{cat.description}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <div className="flex justify-end space-x-2">
                                                <button onClick={() => { setCurrentCategory(cat); setIsModalOpen(true); }} className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"><Edit2 className="h-4 w-4" /></button>
                                                <button onClick={() => handleDelete(cat.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="h-4 w-4" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden border border-gray-100">
                        <div className="p-6 border-b border-gray-50 flex justify-between items-center">
                            <h3 className="text-xl font-bold text-gray-900">{currentCategory.id ? 'Edit Price Level' : 'New Price Level'}</h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-900 transition-colors">
                                <X className="h-6 w-6" />
                            </button>
                        </div>
                        <form onSubmit={handleSave} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Ticket Type (e.g., VIP)</label>
                                <input
                                    type="text"
                                    required
                                    value={currentCategory.name}
                                    onChange={(e) => setCurrentCategory({ ...currentCategory, name: e.target.value })}
                                    className="w-full px-4 py-3 bg-gray-50 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-gray-900 font-medium"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Price (INR)</label>
                                <input
                                    type="number"
                                    required
                                    value={currentCategory.price}
                                    onChange={(e) => setCurrentCategory({ ...currentCategory, price: e.target.value })}
                                    className="w-full px-4 py-3 bg-gray-50 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-gray-900 font-black"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Description</label>
                                <textarea
                                    value={currentCategory.description}
                                    onChange={(e) => setCurrentCategory({ ...currentCategory, description: e.target.value })}
                                    className="w-full px-4 py-3 bg-gray-50 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-gray-900 font-medium h-20 resize-none"
                                />
                            </div>
                            <div className="pt-2 flex space-x-3">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 px-6 py-3 border border-gray-200 text-gray-600 rounded-xl font-bold hover:bg-gray-50">Cancel</button>
                                <button type="submit" disabled={actionLoading} className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-100">
                                    {actionLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Save Pricing'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TicketCategories;
